/**
 * Shape a `KnowledgeGraphResponse` into what the canvas renderer wants —
 * ROUTE_INVENTORY §2.10 #7.
 *
 * Three decisions live here, and each one is a deliberate flattening of
 * information the API does supply:
 *
 * - **Radius from importance**, in three steps (`starRadius`). Uniform sizing
 *   was the earlier call and it was right while the force layout clumped —
 *   size fought the clumps for attention. With position assigned rather than
 *   negotiated there is nothing left to fight, and three magnitudes are what
 *   let the eye find a subject's main concepts before reading any labels.
 * - **Orphan is a boolean.** `is_orphaned` may be `null` from the API; the
 *   renderer wants a definite fill, so null coerces to false. An unknown
 *   orphan state is drawn as connected rather than as isolated, because the
 *   isolated fill is a claim ("nothing links here") and the connected one is
 *   the absence of one.
 * - **Link weight defaults to 0.5.** `weight` is a 0–1 similarity that drives
 *   per-link rest distance in the force simulation. A null weight is missing
 *   data, not zero similarity — and zero would spring the two nodes as far
 *   apart as the graph allows, which reads as "these are unrelated" when the
 *   truth is that nobody measured.
 *
 * Extracted out of `KnowledgeArchivePage` so the mapping can be table-tested.
 * The page's story asserts it is WIRED; `decorateGraph.test.ts` covers the
 * cases, which would otherwise all render as the same field of dots.
 */

import type { KnowledgeGraphResponse } from '@/lib/api-client';
import {
  isEstimated,
  memoryTier,
  retrievability,
  type MemoryTier,
} from '@/lib/mindspace/memoryTier';
import { lessonKeyOf } from '@/lib/mindspace/groupConcepts';

/**
 * Node radius. Uniform size was right for the force graph - size there fought
 * the clumping for the eye's attention - but the pinned layout has no clumping
 * to fight, and a graph where every node is the same size has thrown away the
 * one thing it knows about prominence.
 *
 * 3.2 to 5.6, narrowed from 2.6 to 6.4: at the bottom a 2.6 dot read as a
 * speck of noise at overview, and at the top 6.4 let a few hubs dominate a
 * cluster. Size is still a rank; it just no longer shouts or whispers.
 */
export const NODE_RADIUS_MIN = 3.2;
export const NODE_RADIUS_MAX = 5.6;

/** Kept for the Storybook `fixed` layout seam, which sizes its ring by it. */
export const NODE_RADIUS = 5;

/** Importance -> radius, in three steps so size reads as a rank rather than
 *  as a continuous and therefore invisible gradient. */
export function starRadius(importance: number | null | undefined): number {
  if (importance == null) return NODE_RADIUS_MIN;
  if (importance >= 0.66) return NODE_RADIUS_MAX;
  if (importance >= 0.33) return (NODE_RADIUS_MIN + NODE_RADIUS_MAX) / 2;
  return NODE_RADIUS_MIN;
}

/** Rest distance for a link the API gave no similarity for. */
export const DEFAULT_LINK_WEIGHT = 0.5;

export interface DecoratedNode {
  id: string;
  name: string;
  size: number;
  isOrphan: boolean;
  /** Paused by the student: out of reviews, drawn as a hollow ring. */
  isPaused: boolean;
  /** Generated into Mindspace but not studied yet: drawn lighter. */
  notStarted: boolean;
  /** Weight: a fading memory is drawn lighter. See `tierAlpha`. */
  tier: MemoryTier;
  /**
   * Which cluster this concept belongs to — a LESSON ID, not a name. See
   * `lessonKeyOf`; the display name is resolved at paint time.
   */
  subject: string;
  /** Kept as well as `size`: the layout orders a cluster by it, and the
   *  three-step magnitude is too coarse to sort with. */
  importance: number | null;
  /** Chance of recall now, 0..1, or null when there is nothing to measure. */
  recall: number | null;
  /** True when `recall` is estimated from study, not measured by a review. */
  recallEstimated: boolean;
}

/** The three kinds the backend writes; `null` for older or unknown rows. */
export type LinkKind = 'co_occurrence' | 'similarity' | 'prerequisite' | null;

export interface DecoratedLink {
  source: string;
  target: string;
  weight: number;
  /**
   * The edge kind, which decides whether the line is ever drawn. Dropping
   * this was what forced the old graph to render all ~5,000 edges equally:
   * with no way to tell a lesson clique from a prerequisite, it had to treat
   * them the same.
   */
  kind: LinkKind;
}

export interface DecoratedGraph {
  nodes: DecoratedNode[];
  links: DecoratedLink[];
  /** Per lesson, its concepts in teaching order (the lesson's arm and its
   *  constellation). Empty when the backend could not say. */
  lessonPaths: Record<string, string[]>;
}

const KINDS = new Set(['co_occurrence', 'similarity', 'prerequisite']);

function linkKind(type: string | null | undefined): LinkKind {
  return type && KINDS.has(type) ? (type as LinkKind) : null;
}

/**
 * Which kind wins when one pair of concepts carries several.
 *
 * Lower is more meaningful, so a pair that is both a lesson clique and a
 * measured similarity is drawn as the similarity — the fact worth a line.
 * Picking the other way round would hide it, since co-occurrence is never
 * drawn: `null` ranks last because an edge of unknown kind is the weakest
 * claim of the four.
 */
const KIND_RANK: Record<string, number> = {
  prerequisite: 0,
  similarity: 1,
  co_occurrence: 2,
};
const kindRank = (kind: LinkKind): number =>
  kind == null ? 3 : KIND_RANK[kind];

/** Unordered pair key: an edge a→b and an edge b→a are one line. */
function pairKey(a: string, b: string): string {
  return a < b ? `${a}\u0000${b}` : `${b}\u0000${a}`;
}

/**
 * One line per pair of concepts, and only between concepts that are here.
 *
 * Measured against a live archive of 49,962 edges, every case below is real:
 *
 * - **1,024 pairs are written in BOTH directions.** Drawn raw that is two
 *   lines, and because `linkCurvature` bows a line by its source→target
 *   direction the two arc opposite ways — a lens between the two nodes rather
 *   than an edge. 5,297 duplicate rows in total once same-kind repeats are
 *   counted.
 * - **4,549 pairs carry more than one kind**, so the same two concepts stack a
 *   co-occurrence, a similarity and sometimes a prerequisite line.
 * - **134 edges point at a concept that is not in the response** — deleted,
 *   archived, or past the node cap. `react-force-graph` resolves endpoints
 *   against the node array, so these are edges into nothing.
 * - **10 edges are a concept to itself**, which draws as a loop over the node.
 *
 * `utils/graphValidation.validateGraphData` dedupes pairs too, but it ignores
 * `edge_type` and keeps whichever row Mongo returned first — so on the 4,549
 * mixed pairs it would keep the co-occurrence and discard the similarity,
 * silently deleting the edges this view is built to show. That is why the rule
 * lives here, with the precedence spelled out.
 */
function dedupeLinks(
  links: readonly {
    source: string;
    target: string;
    weight: number | null | undefined;
    type?: string | null;
  }[],
  nodeIds: ReadonlySet<string>
): DecoratedLink[] {
  const best = new Map<string, DecoratedLink>();
  for (const link of links) {
    const { source, target } = link;
    if (source === target) continue;
    if (!nodeIds.has(source) || !nodeIds.has(target)) continue;

    const kind = linkKind(link.type);
    const weight = link.weight ?? DEFAULT_LINK_WEIGHT;
    const key = pairKey(source, target);
    const held = best.get(key);
    if (held == null) {
      best.set(key, { source, target, weight, kind });
      continue;
    }
    const rank = kindRank(kind);
    const heldRank = kindRank(held.kind);
    // A better kind replaces outright; an equal one only lifts the weight, so
    // the drawn line is the strongest claim made about the pair.
    if (rank < heldRank) best.set(key, { source, target, weight, kind });
    else if (rank === heldRank && weight > held.weight) held.weight = weight;
  }
  return [...best.values()];
}

export function decorateGraph(
  api: KnowledgeGraphResponse | undefined,
  now: Date = new Date()
): DecoratedGraph {
  if (!api) return { nodes: [], links: [], lessonPaths: {} };
  return {
    nodes: api.nodes.map(node => ({
      id: node.id,
      name: node.name,
      size: starRadius(node.importance),
      isOrphan: !!node.is_orphaned,
      isPaused: !!node.is_archived,
      notStarted: node.evidence_status === 'not_started',
      tier: memoryTier(node, now),
      subject: lessonKeyOf(node),
      importance: node.importance ?? null,
      recall: retrievability(node, now),
      recallEstimated: isEstimated(node),
    })),
    links: dedupeLinks(api.links, new Set(api.nodes.map(n => n.id))),
    lessonPaths: api.lesson_paths ?? {},
  };
}
